import React, {
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Outlet,
  useLocation,
} from "react-router-dom";
import { InternalContent } from "@components/library";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import { useTranslation } from "@helpers/translate";
import {
  PROFILE_VERIFICATION_PAGE,
  VERIFICATION,
} from "@constants";

import StepOneIcon from "@assets/icons/stepOneIcon";
import StepTwoIcon from "@assets/icons/stepTwoIcon";
import StepFourIcon from "@assets/icons/stepFourIcon";

import { initVerification } from "@actions/verification/initVerification";

import "./index.sass";

const STEP_ICONS = [
  StepOneIcon,
  StepTwoIcon,
  StepFourIcon,
];

const STEP_ORDER = [
  "personal-information",
  "id-verification",
  "result",
];

const getStepFromPathname = (pathname) => {
  const match = pathname.match(
    /\/profile\/verification\/([^/?#]+)/,
  );

  const step = match?.[1];

  return STEP_ORDER.includes(step)
    ? step
    : null;
};

export const ProfileVerificationLayout = () => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector(
    (state) => state.user.user || {},
  );

  const didInitOnEnterRef = useRef(false);

  const activeStep = getStepFromPathname(
    location.pathname,
  );

  const activeIndex = activeStep
    ? STEP_ORDER.indexOf(activeStep)
    : -1;

  const isLanding =
    location.pathname ===
    PROFILE_VERIFICATION_PAGE;

  const isStarted =
    !isLanding && activeIndex >= 0;

  const isIdVerification =
    location.pathname.includes(
      "id-verification",
    );

  const steps = useMemo(
    () => [
      {
        id: "personal-information",
        labelKey:
          "verification.header.personal",
      },
      {
        id: "id-verification",
        labelKey: "verification.header.id",
      },
      {
        id: "result",
        labelKey:
          "verification.header.done",
      },
    ],
    [],
  );

  useEffect(() => {
    if (
      !didInitOnEnterRef.current &&
      user &&
      Object.keys(user).length > 0
    ) {
      dispatch({
        type: VERIFICATION.RESET,
      });

      dispatch(initVerification(user));

      didInitOnEnterRef.current = true;
    }
  }, [dispatch, user]);

  return (
    <InternalContent>
      <div className="profile-verification">
        <div className="profile-verification__title-mobile">
          {__(
            "verification.header.title",
          )}
        </div>

        <div className="profile-verification__header">
          <div className="profile-verification__title">
            {__(
              "verification.header.title",
            )}
          </div>

          <div
            className={`profile-verification__steps ${
              isLanding
                ? "is-landing"
                : ""
            } ${
              isStarted
                ? "is-started"
                : ""
            }`}
            role="navigation"
            aria-label="Verification steps"
          >
            {steps.map((step, index) => {
              const isDone =
                index < activeIndex;

              const isActive =
                index === activeIndex;

              const StepIcon =
                STEP_ICONS[index];

              return (
                <div
                  key={step.id}
                  className={`profile-verification__step ${
                    isDone
                      ? "is-done"
                      : ""
                  } ${
                    isActive
                      ? "is-active"
                      : ""
                  }`}
                >
                  <div className="profile-verification__step-container">
                    <span
                      className="profile-verification__step-icon-wrap"
                      aria-hidden
                    >
                      <span className="profile-verification__step-icon">
                        {StepIcon ? (
                          <StepIcon className="profile-verification__step-icon-svg" />
                        ) : null}
                      </span>
                    </span>

                    <span className="profile-verification__step-label">
                      {__(
                        step.labelKey,
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className={`profile-verification__content ${
            isIdVerification
              ? "profile-verification__content--no-bg"
              : ""
          }`}
        >
          <Outlet />
        </div>
      </div>
    </InternalContent>
  );
};

export default ProfileVerificationLayout;
